import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { Panel } from '@/components/erp/primitives'
import { fmtNumber } from '@/shared/lib/format'
import { explain } from '@/shared/api/problem'
import { getRun, reportForRun, startRun } from '@/features/advisory/api'

/**
 * The council — a shared surface for "what the numbers are saying, and what to do".
 *
 * ## Why the rules live in each feature and only the shell lives here
 *
 * A turnout finding and a polling finding are not the same kind of claim: one is about an
 * afternoon that can still be changed, the other about what a published number is allowed
 * to bear. Sharing the rule engines would force one vocabulary onto both and produce
 * advice that is generic enough to be useless. What they DO share is the contract — a
 * ranked list of claims, each carrying its evidence and a move — and the ask box with its
 * history. That is what is here.
 *
 * ## The two guarantees, in both consoles
 *
 * **Nothing is invented.** Every sentence a council emits is built from a figure the page
 * was handed. There is no model call, so there is no possibility of a confident number
 * that the platform does not hold.
 *
 * **Silence is an answer.** A question that matches a topic with nothing remarkable in it
 * says so, and one that matches no topic says THAT instead — the two are different, and a
 * reader deciding whether to act needs to be able to tell them apart.
 */

export type Severity = 'critical' | 'serious' | 'watch' | 'good'

export interface Finding {
  id: string
  severity: Severity
  /** The claim, in the reader's language rather than the schema's. */
  headline: string
  /** The figures the claim rests on, verbatim, so it can be checked. */
  evidence: string
  /** What to do about it — specific enough to hand to somebody. */
  action: string
  /**
   * How much this is worth, for ranking. Votes in the turnout console, interviews in the
   * polling one; `stakeUnit` names it. Zero means "ranked by severity alone".
   */
  votesAtStake: number
  /** Chips under the card — cell codes, option labels, whatever the finding points at. */
  cellCodes?: string[]
}

/**
 * One question, and what came back from the advisor.
 *
 * `status` is the whole point of this shape. A run takes anywhere from seventeen seconds to
 * three minutes, so the three outcomes that used to be indistinguishable — still working,
 * answered, broke — each need their own state, and the id has to be persisted so a reload in
 * the middle of a run rejoins it rather than losing it.
 */
export interface Answer {
  question: string
  askedAt: string
  /** The advisor run. Null when the run could not be started at all. */
  runId: string | null
  status: 'PENDING' | 'DONE' | 'FAILED'
  /** The report, once there is one. */
  headline?: string
  summary?: string
  body?: string
  confidence?: string
  error?: string
  /**
   * The local rule engine's reading, shown IMMEDIATELY while the model works.
   *
   * The deterministic council can answer in a millisecond from figures already on the page.
   * The advisor takes minutes and can say things no rule anticipates. Neither replaces the
   * other, so the box shows the fast answer first and replaces nothing when the considered
   * one lands — it appends it, and labels which is which.
   */
  local?: { headline: string; action: string } | null
  /** Stored before this panel called the advisor at all — see `migrate`. */
  legacy?: boolean
}

const SEVERITY_RANK: Record<Severity, number> = { critical: 0, serious: 1, watch: 2, good: 3 }

const SEVERITY_UI: Record<Severity, { ring: string; chip: string; word: string }> = {
  critical: { ring: 'border-l-rose-600',    chip: 'bg-rose-600 text-white',    word: 'Critical' },
  serious:  { ring: 'border-l-amber-500',   chip: 'bg-amber-500 text-black',   word: 'Serious' },
  watch:    { ring: 'border-l-sky-500',     chip: 'bg-sky-500 text-white',     word: 'Watch' },
  good:     { ring: 'border-l-emerald-600', chip: 'bg-emerald-600 text-white', word: 'Working' },
}

/** Severity first, then what it is worth. Severity words are opinions; the stake is not. */
export function rank(findings: Finding[]): Finding[] {
  return [...findings].sort((a, b) =>
    SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity] || b.votesAtStake - a.votesAtStake)
}

export function CouncilBoard({
  findings, title, description, stakeUnit = 'votes', emptyMessage, hideTotal,
}: {
  findings: Finding[]
  title: string
  description: string
  /** Singular noun for the stake — "vote", "interview". Pluralised by adding an s. */
  stakeUnit?: string
  emptyMessage: string
  /**
   * Suppress the "N in play" total.
   *
   * For a board whose findings come from ONE domain the total is a real quantity. On a
   * cross-platform board it is not: engagements, incidents and votes are different things,
   * and adding them produces a confident number that measures nothing. The per-card stakes
   * still rank the list; only the sum is withheld.
   */
  hideTotal?: boolean
}) {
  const unit = stakeUnit.replace(/s$/, '')
  const total = findings.reduce((n, f) => n + f.votesAtStake, 0)
  return (
    <Panel
      title={title}
      description={description}
      right={total > 0 && !hideTotal ? (
        <span className="text-erp-xs text-muted-foreground tabular-nums">
          {fmtNumber(total)} {total === 1 ? unit : `${unit}s`} in play
        </span>
      ) : undefined}
      bodyClassName="p-0"
    >
      <ul className="divide-y divide-border">
        {findings.length === 0 && (
          <li className="p-4 text-erp-sm text-muted-foreground">{emptyMessage}</li>
        )}
        {findings.map((f) => {
          const ui = SEVERITY_UI[f.severity]
          return (
            <li key={f.id} className={cn('p-4 border-l-[3px]', ui.ring)}>
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <span className={cn('rounded px-1.5 py-0.5 text-erp-xs font-medium', ui.chip)}>
                  {ui.word}
                </span>
                {f.votesAtStake > 0 && (
                  <span className="text-erp-xs text-muted-foreground tabular-nums">
                    ≈ {fmtNumber(f.votesAtStake)} {f.votesAtStake === 1 ? unit : `${unit}s`}
                  </span>
                )}
              </div>
              <p className="text-erp-sm font-medium">{f.headline}</p>
              <p className="text-erp-xs text-muted-foreground mt-1">{f.evidence}</p>
              <p className="text-erp-sm mt-2">{f.action}</p>
              {f.cellCodes && f.cellCodes.length > 0 && (
                <p className="mt-2 flex flex-wrap gap-1">
                  {f.cellCodes.map((c) => (
                    <span key={c}
                          className="rounded border border-border px-1.5 py-0.5 text-erp-xs">
                      {c}
                    </span>
                  ))}
                </p>
              )}
            </li>
          )
        })}
      </ul>
    </Panel>
  )
}

/**
 * The ask box — a real Advisory run, with the board's own figures as its context.
 *
 * ## What was wrong before, and it was the whole feature
 *
 * The first version of this panel answered from the local rule engine and nothing else. It
 * looked like it worked: you typed a question, a sentence appeared. But no run was ever
 * started, the question never reached the advisor, and any question outside the keyword table
 * came back as "no rule covers that" — which reads as a considered refusal rather than as the
 * absence of a request. A console that appears to consult a model and does not is worse than
 * one with no ask box at all, because the reader believes an answer was reasoned about.
 *
 * Now `ask` starts a real `ASK` run. That playbook's brief recipe has no contributors and its
 * user prompt is literally `{{question}}` — so what is sent IS what the model sees, which is
 * why `context()` matters: the board's figures are composed into the question, and the model
 * answers about this window, this poll, this hour, rather than in general.
 *
 * ## Three states, kept distinct on purpose
 *
 * PENDING with a spinner and the elapsed count; DONE with the report; FAILED with the
 * advisor's own error. A run takes 17 seconds to 3 minutes, so the id is persisted with the
 * question — reloading the page mid-run rejoins that run instead of stranding it, which is
 * the failure this feature exists to avoid everywhere else.
 */
export function AskPanel({
  storageKey, suggestions, placeholder, onAsk, description, context, scopeNote,
}: {
  storageKey: string
  suggestions: string[]
  placeholder: string
  /** The instant local reading, shown while the advisor works. */
  onAsk: (question: string) => { headline: string; action: string } | null
  description: string
  /** The board's own figures, composed at ask time. This is what makes the answer specific. */
  context: () => string
  /** What the run is scoped to, shown under the box so the reader knows what it can see. */
  scopeNote?: string
}) {
  const [question, setQuestion] = useState('')
  const [history, setHistory] = useState<Answer[]>([])
  const [starting, setStarting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)
  const historyRef = useRef<Answer[]>([])
  historyRef.current = history

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(storageKey)
      setHistory(raw ? (JSON.parse(raw) as unknown[]).map(migrate) : [])
    } catch { setHistory([]) }
  }, [storageKey])

  const persist = (next: Answer[]) => {
    setHistory(next)
    // Capped: an unbounded list eventually throws a quota error on write, and that failure
    // would land on the answer rather than on the history.
    try { window.localStorage.setItem(storageKey, JSON.stringify(next.slice(0, 40))) }
    catch { /* full or blocked */ }
  }

  /*
     One poller for every pending run, including ones started before a reload.

     Polling per-card would open an interval per question and leave them running after the
     card resolved. This walks the pending set on each tick, and the tick only exists while
     something is pending — an idle panel makes no requests at all.
  */
  const pending = history.filter((a) => a.status === 'PENDING' && a.runId)
  useEffect(() => {
    if (pending.length === 0) return
    let cancelled = false
    const id = window.setInterval(async () => {
      setTick((n) => n + 1)
      for (const entry of historyRef.current.filter((a) => a.status === 'PENDING' && a.runId)) {
        try {
          const run = await getRun(entry.runId!)
          if (cancelled) return
          const done = run.status === 'COMPLETED'
          const failed = run.status === 'FAILED' || run.status === 'CANCELLED'
          if (!done && !failed) continue

          let patch: Partial<Answer> = failed
            ? { status: 'FAILED', error: run.errorMessage ?? run.errorCode ?? `The run ended as ${run.status}.` }
            : { status: 'DONE' }

          if (done) {
            try {
              const report = await reportForRun(run.id)
              patch = {
                status: 'DONE',
                headline: report.headline,
                summary: report.executiveSummary,
                body: report.bodyMarkdown ?? undefined,
                confidence: report.confidence,
              }
            } catch {
              // COMPLETED with no readable report is its own outcome, not a success. Saying
              // "answered" over an empty card is the silent failure this module forbids.
              patch = {
                status: 'FAILED',
                error: 'The run completed but produced no readable report. It is on the Advisory console with its full trace.',
              }
            }
          }
          persist(historyRef.current.map(
            (a) => (a.runId === entry.runId ? { ...a, ...patch } : a)))
        } catch {
          /* A transient read failure must not kill the poll — the next tick retries. */
        }
      }
    }, 4000)
    return () => { cancelled = true; window.clearInterval(id) }
  }, [pending.length, storageKey])

  const submit = async (q: string) => {
    const text = q.trim()
    if (!text || starting) return
    setStarting(true)
    setError(null)
    const local = onAsk(text)
    const askedAt = new Date().toISOString()
    try {
      /*
         The board's figures go INTO the question, because the ASK playbook assembles no brief
         of its own. Without this the model is asked "where should I send the vans?" with no
         idea what vans, whose, or how far behind anything is — and answers in generalities
         that look like insight and are not.
      */
      const run = await startRun({
        playbookCode: 'ASK',
        question: `${context()}\n\nQuestion: ${text}`,
        title: text.slice(0, 120),
      })
      persist([{ question: text, askedAt, runId: run.id, status: 'PENDING', local },
        ...historyRef.current])
      setQuestion('')
    } catch (e) {
      const message = explain(e)
      setError(message)
      // Recorded as FAILED rather than dropped: a question that could not be asked is still
      // something this desk wanted to know, and it belongs in the history with the reason.
      persist([{ question: text, askedAt, runId: null, status: 'FAILED', error: message, local },
        ...historyRef.current])
      setQuestion('')
    } finally {
      setStarting(false)
    }
  }

  const clear = () => {
    persist([])
    try { window.localStorage.removeItem(storageKey) } catch { /* blocked */ }
  }

  return (
    <div className="space-y-4">
      <Panel title="Ask the council" description={description}>
        <form onSubmit={(e) => { e.preventDefault(); void submit(question) }} className="space-y-2">
          <input
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder={placeholder}
            disabled={starting}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-erp-sm disabled:opacity-60"
            aria-label="Ask the council a question"
          />
          <button type="submit" disabled={starting || !question.trim()}
                  className="w-full rounded-md bg-primary text-primary-foreground px-3 py-2 text-erp-sm font-medium hover:opacity-90 disabled:opacity-50">
            {starting ? 'Sending to the council…' : 'Ask'}
          </button>
        </form>
        {error && <p className="mt-2 text-erp-xs text-rose-600 dark:text-rose-400">{error}</p>}
        <div className="mt-3 flex flex-wrap gap-1">
          {suggestions.map((q) => (
            <button key={q} type="button" disabled={starting} onClick={() => void submit(q)}
                    className="rounded-full border border-border px-2 py-1 text-erp-xs text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50">
              {q}
            </button>
          ))}
        </div>
        <p className="mt-3 text-erp-xs text-muted-foreground">
          This starts a real run on the Axis Advisory council, with the figures on this page as
          its context. Answers take from about twenty seconds to a few minutes; you can leave
          this page and the answer will be here when you come back.
          {scopeNote ? ` ${scopeNote}` : ''}
        </p>
      </Panel>

      <Panel
        title="Asked this session"
        description="Questions and their answers, kept in this browser. Every run is also on the Advisory console with its full trace."
        right={history.length > 0 ? (
          <button type="button" onClick={clear}
                  className="text-erp-xs text-muted-foreground hover:text-foreground">
            Clear
          </button>
        ) : undefined}
        bodyClassName="p-0"
      >
        {history.length === 0 ? (
          <p className="p-4 text-erp-sm text-muted-foreground">
            Nothing asked yet. Questions are kept with their answers, so you can see what this
            desk was worrying about and when — which is usually the useful part afterwards.
          </p>
        ) : (
          <ul className="divide-y divide-border max-h-[560px] overflow-y-auto">
            {history.map((a, i) => (
              <AnswerCard key={`${a.askedAt}-${i}`} answer={a} tick={tick} />
            ))}
          </ul>
        )}
      </Panel>
    </div>
  )
}

/**
 * Bring a stored entry up to the current shape.
 *
 * The first version of this panel stored `{finding, fallback}` and no `status`. Read back
 * as-is, those entries render a question with nothing under it — which looks exactly like a
 * run that vanished, and is the one thing this panel must never look like. They are mapped to
 * a resolved card carrying whatever text they held, and labelled as coming from the rules
 * rather than from the council, because that is where they came from.
 */
function migrate(raw: unknown): Answer {
  const a = raw as Partial<Answer> & {
    finding?: { headline?: string; action?: string } | null
    fallback?: string
  }
  if (a.status === 'PENDING' || a.status === 'DONE' || a.status === 'FAILED') return a as Answer
  return {
    question: a.question ?? '(question not recorded)',
    askedAt: a.askedAt ?? new Date(0).toISOString(),
    runId: null,
    status: 'DONE',
    local: a.finding?.headline
      ? { headline: a.finding.headline, action: a.finding.action ?? '' }
      : null,
    summary: a.finding ? undefined : a.fallback,
    legacy: true,
  }
}

function AnswerCard({ answer, tick }: { answer: Answer; tick: number }) {
  const [open, setOpen] = useState(false)
  // `tick` is read so the elapsed counter re-renders with the poll rather than needing its
  // own timer; it has no other purpose.
  void tick
  const elapsed = Math.max(0, Math.round((Date.now() - new Date(answer.askedAt).getTime()) / 1000))

  return (
    <li className="p-3">
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-erp-sm font-medium">{answer.question}</p>
        <span className="text-erp-xs text-muted-foreground shrink-0 tabular-nums">
          {new Date(answer.askedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </span>
      </div>

      {answer.local && (
        <div className="mt-1.5 border-l-2 border-border pl-2">
          <p className="text-erp-xs uppercase tracking-wide text-muted-foreground">
            From the figures on this page
          </p>
          <p className="text-erp-xs mt-0.5">{answer.local.headline}</p>
        </div>
      )}

      {answer.status === 'PENDING' && (
        <div className="mt-2 flex items-start gap-2 text-erp-xs text-muted-foreground">
          {/* `items-start` and a single text node: with `items-center` and inline spans the
              elapsed counter wrapped onto its own line and the spinner floated beside a gap. */}
          <span className="mt-0.5 inline-block h-3 w-3 shrink-0 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-muted-foreground"
                aria-hidden />
          <span>
            The council is working — <span className="tabular-nums">{elapsed}s</span> elapsed.
            A considered answer takes from twenty seconds to a few minutes.
          </span>
        </div>
      )}

      {answer.status === 'FAILED' && (
        <p className="mt-2 text-erp-xs text-rose-600 dark:text-rose-400">
          {answer.error ?? 'The run did not finish.'}
        </p>
      )}

      {answer.status === 'DONE' && (
        <div className="mt-2 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className={cn('rounded px-1.5 py-0.5 text-erp-xs font-medium',
              answer.legacy
                ? 'bg-muted text-muted-foreground'
                : 'bg-primary/10 text-primary')}>
              {answer.legacy ? 'Answered from the figures only' : 'Council'}
            </span>
            {answer.confidence && (
              <span className="text-erp-xs text-muted-foreground">
                confidence {answer.confidence.toLowerCase()}
              </span>
            )}
          </div>
          {answer.headline && <p className="text-erp-sm font-medium">{answer.headline}</p>}
          {answer.summary && <p className="text-erp-xs whitespace-pre-line">{answer.summary}</p>}
          {answer.body && (
            <>
              <button type="button" onClick={() => setOpen((v) => !v)}
                      className="text-erp-xs text-muted-foreground hover:text-foreground underline">
                {open ? 'Hide the full answer' : 'Read the full answer'}
              </button>
              {open && (
                <p className="text-erp-xs whitespace-pre-line text-muted-foreground">
                  {answer.body}
                </p>
              )}
            </>
          )}
        </div>
      )}
    </li>
  )
}

/**
 * Match a typed question to a local finding, for the instant reading beside the run.
 *
 * Keyword matching, and openly so. It is no longer the answer — the advisor is — but it is
 * what fills the gap while a run takes two minutes, and for "what should I do right now" it
 * is often the better of the two anyway, because it is built from figures rather than prose.
 * Returning null is fine and common: the card then shows only the council's answer.
 */
export function route(
  question: string,
  findings: Finding[],
  routes: { id: string; words: string[] }[],
): { headline: string; action: string } | null {
  const q = question.toLowerCase().trim()
  if (!q) return null
  const scored = routes
    .map((r) => ({ id: r.id, hits: r.words.filter((w) => q.includes(w)).length }))
    .filter((r) => r.hits > 0)
    .sort((a, b) => b.hits - a.hits)
  for (const s of scored) {
    const hit = findings.find((f) => f.id === s.id)
    if (hit) return { headline: hit.headline, action: hit.action }
  }
  return null
}
